<script setup lang="ts">
import DataLayerSpecs from '@/components/references/DataLayerSpecs.vue'
import GuidebookRemedySpecs from '@/components/references/GuidebookRemedySpecs.vue'
import UIBody from '@/components/ui/UIBody.vue'
import UIHeading from '@/components/ui/UIHeading.vue'
import UIPill from '@/components/ui/UIPill.vue'
import UITextButton from '@/components/ui/UITextButton.vue'
import { useAnalytics } from '@/composables/useAnalytics'
import useLanguage, { useInlineLanguage } from '@/composables/useLanguage'
import { useReferencesStore } from '@/store/references'
import { IPillLinkItem } from '@lifk/types'

const props = defineProps<{
  /** What item should be rendered in the tooltip? */
  item: IPillLinkItem
}>()

const i18n = useLanguage('RemedyTooltip')
const { t } = useInlineLanguage()
const referencesStore = useReferencesStore()
const { trackButtonClick } = useAnalytics()

const referencesLinkClicked = () => {
  referencesStore.openReference(
    props.item.linkType,
    props.item.key,
    'references'
  )
  trackButtonClick({
    action: 'viewed_guidebook',
    location: 'references_sidebar',
    value: {
      key: props.item.key,
      name: props.item.name['en-us']
    }
  })
}
</script>

<template>
  <div data-testid="references-tooltip" tabindex="0" class="min-w-[280px] py-2">
    <div class="flex justify-between pb-inline-default">
      <UIHeading
        variant="serif"
        tag="h4"
        :text="t(item.name)"
        tabindex="0"
        style="white-space: initial"
      ></UIHeading>
      <UIPill
        :text="t(item.categoryName)"
        size="sm"
        tabindex="0"
        class="text-right ml-3 h-5"
        :raw-colors="{
          bgColor: item.pillColor,
          textColor: item.textColor
        }"
      />
    </div>
    <div class="space-y-stack-relaxed">
      <UIBody v-if="!!item.description" weight="regular">
        <p tabindex="0" style="white-space: initial">
          {{ t(item.description) }}
        </p>
      </UIBody>
      <GuidebookRemedySpecs
        v-if="item.linkType === 'remedy'"
        :budgets="item.budgets"
        :footprint="item.footprint"
        :footprint-placement="item.footprintPlacement"
        tabindex="0"
      />
      <DataLayerSpecs
        v-else-if="item.linkType === 'dataLayer' && item.dataLayer"
        :data-layer="item.dataLayer"
        position="tooltip"
        tabindex="0"
      />
      <UITextButton
        v-if="!referencesStore.isOpen"
        icon="Book"
        variant="outline"
        color="neutral"
        :text="i18n.learnMore"
        @click.stop.prevent="referencesLinkClicked"
      />
    </div>
  </div>
</template>
